import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import type { QuotaType } from '@/config/plans'
import { kbdSubmitProps } from '@/hooks/formKeyboardNav'
import { getConsoleUrl, isConsoleDeployment } from '@/utils/deployment'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

export interface QuotaExceededPopoverProps {
	quotaKey: QuotaType
	used: number
	total: number
	billingHref: string
	label?: string
	children: ReactNode
}

export function QuotaExceededPopover({
	quotaKey,
	used,
	total,
	billingHref,
	label,
	children,
}: QuotaExceededPopoverProps) {
	const isConsole = isConsoleDeployment()
	const targetHref = !isConsole && billingHref.startsWith('/') ? `${getConsoleUrl()}${billingHref}` : billingHref

	const isExternal = targetHref.startsWith('http://') || targetHref.startsWith('https://')

	return (
		<HoverCard closeDelay={150}>
			<HoverCardTrigger asChild>{children}</HoverCardTrigger>
			<HoverCardContent align='end' className='w-80 z-300'>
				<div className='space-y-4'>
					<div className='space-y-1'>
						<p className='text-body font-semibold text-status-warning-text'>{quotaKey} limit reached</p>
						<p className='text-control text-muted-foreground'>
							{label ??
								`You've used ${used} of ${total} ${quotaKey.toLowerCase()}. Upgrade your plan or archive unused resources to create more.`}
						</p>
					</div>
					<Button asChild size='sm' className='w-full' {...kbdSubmitProps()}>
						{isExternal ? (
							<a href={targetHref} target='_blank' rel='noopener noreferrer'>
								View billing
							</a>
						) : (
							<Link to={targetHref}>View billing</Link>
						)}
					</Button>
				</div>
			</HoverCardContent>
		</HoverCard>
	)
}
